/**
 * Canonicalize web URLs so highlights of "the same page" group together.
 *
 * NIP-84 asks publishers to strip trackers from `r` tags; this does that and
 * more: lowercase host, no hash fragment, no trailing slash, no default
 * port, tracking params removed, remaining query params sorted.
 */

const TRACKING_PARAMS = new Set([
  'fbclid',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'mc_cid',
  'mc_eid',
  'igshid',
  'ref',
  'ref_src',
  'ref_url',
  'spm',
  'scm',
  'si',
  'feature',
  'oly_anon_id',
  'oly_enc_id',
  'vero_id',
  'wickedid',
  'twclid',
  'msclkid',
]);

function isTrackingParam(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.startsWith('utm_') || TRACKING_PARAMS.has(lower);
}

/** Canonical form of a URL, or null if it isn't a valid http(s) URL. */
export function normalizeUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  url.hostname = url.hostname.toLowerCase();
  url.hash = '';

  if ((url.protocol === 'https:' && url.port === '443') || (url.protocol === 'http:' && url.port === '80')) {
    url.port = '';
  }

  // Strip tracking params; sort the rest for a stable canonical form.
  const kept = [...url.searchParams.entries()].filter(([name]) => !isTrackingParam(name));
  kept.sort(([a], [b]) => a.localeCompare(b));
  url.search = '';
  for (const [name, value] of kept) {
    url.searchParams.append(name, value);
  }

  // Drop trailing slashes (except the root path).
  if (url.pathname.length > 1) {
    url.pathname = url.pathname.replace(/\/+$/, '');
  }

  return url.href;
}

/**
 * Candidate `r` tag values to match highlights of a page, covering both the
 * raw and normalized forms (since not every publisher normalizes).
 */
export function urlMatchCandidates(raw: string): string[] {
  const candidates = new Set<string>();
  candidates.add(raw);
  const normalized = normalizeUrl(raw);
  if (normalized) candidates.add(normalized);
  return [...candidates];
}
